<script setup lang="ts">
import HomeHeader from "@/views/home/HomeHeader.vue";
import HomeFooter from "@/views/home/HomeFooter.vue";
import { postLogin } from "@/api/auth/postLogin";
import { LOCAL_STORAGE_KEYS } from "@/constants/storageKeys";
import { alert, useFormBuilder } from "@/services";
import { localize, Validators } from "@/utils";
import { onMounted } from "vue";
import { useRouter } from "vue-router";

const { controls, errors, group, touch, validate, valid, invalid } = useFormBuilder();
group({
  email: { touched: false, value: "", validators: [Validators.required, Validators.email] },
  password: {
    touched: false,
    isShow: false,
    value: "",
    validators: [Validators.required, Validators.minLength(8)],
  },
});

onMounted(init);

/**
 * 초기화
 */
function init() {
  validate();
}
/**
 * 비밀번호 보이기 형식 토글
 * @param key
 */
function togglePassword(key: string) {
  controls[key].isShow = !controls[key].isShow;
}

const navRouter = useRouter();

const done = async() => {
  try {
    const res = await postLogin({loginId: controls.email.value, password: controls.password.value});
    if(res.isSuccess && res.data) {
      localStorage.setItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN, res.data.accessToken);
      localStorage.setItem(LOCAL_STORAGE_KEYS.ROLE_TYPE, res.data.role);
      navRouter.push("/home/about");
      return;
    } else if(res.error){
      if(res.error.code === "E_LOGIN_003") {
        alert(localize('아이디 (이메일 계정) 또는 비밀번호가 잘못되었습니다.<br/>아이디 또는 비밀번호를 정확히 입력해주세요.'));
      }
    }
  } catch (error) {
    alert(localize('서버 연결에 실패하였습니다.<br/>잠시후, 다시 시도해 주세요.'));
  }
}
</script>

<template>
  <div id="wrap" class="r2playground">
    <home-header />

    <main id="ct">
      <div class="container support">
        <div class="support-wrap">
          <ul class="nav nav-tabs support-nav">
            <li class="nav-item">
              <router-link to="" class="nav-link">Home</router-link>
            </li>
            <li class="nav-item">
              <router-link to="" class="nav-link active">Log in</router-link>
            </li>
          </ul>
          <div class="tit-head">
            <h1>Log in</h1>
            <img src="@/assets/home/img/illust-login.png" alt="" />
          </div>
        </div>
        <div class="join-box login-box">
          <div class="step-wrap">
            <img src="@/assets/home/img/logo.svg" alt="" class="" />
          </div>
          <form @submit.prevent="done()" class="join-form login-form">
            <!-- 이메일 입력-->
            <div class="form-group">
              <label class="form-label" for="email">{{ localize("이메일") }}</label>
              <div class="input-group">
                <input
                  type="email"
                  id="email"
                  class="form-control curved"
                  :class="{ 'is-invalid': controls.email.touched && !valid('email') }"
                  v-model="controls.email.value"
                  @focusout="touch('email')"
                  :placeholder="localize('아이디로 사용할 이메일 주소 입력')"
                />
                <!--    이메일 에러 메시지-->
                <div v-show="controls.email.touched && errors?.email?.required" class="invalid-feedback">
                  * {{ localize("이메일을 입력하세요.") }}
                </div>
                <div v-show="controls.email.touched && errors?.email?.email" class="invalid-feedback">
                  * {{ localize("올바른 이메일을 입력하세요.") }}
                </div>
              </div>
            </div>
            <!-- //이메일 입력-->

            <!-- 비밀번호 입력-->
            <div class="form-group">
              <label class="form-label" for="pw">{{ localize("비밀번호") }}</label>
              <div class="input-group">
                <input
                  id="pw"
                  class="form-control curved"
                  :class="{
                    'is-invalid': controls.password.touched && !valid('password'),
                    active: controls.password.isShow,
                  }"
                  v-model="controls.password.value"
                  @focusout="touch('password')"
                  @keyup.enter="done"
                  :type="controls.password.isShow ? 'text' : 'password'"
                  :placeholder="localize('영문, 숫자, 특수문자의 조합 8자리 이상 입력')"
                />
                <div class="visibility">
                  <i
                    class="icon"
                    @click="togglePassword('password')"
                    :class="controls.password.isShow ? 'icon-eye-open' : 'icon-eye-close'"
                  ></i>
                </div>
                <!--    비밀번호 에러 메시지-->
                <div v-show="controls.password.touched && errors?.password?.required" class="invalid-feedback">
                  * {{ localize("비밀번호를 입력하세요.") }}
                </div>
                <div
                  v-show="controls.password.touched && (errors?.password?.minLength || errors?.password?.password)"
                  class="invalid-feedback"
                >
                  * {{ localize("영문, 숫자, 특수문자 중 2종의 조합으로 8자리 이상 입력하세요.") }}
                </div>
              </div>
            </div>
            <!-- //비밀번호 입력-->
          </form>

          <!-- 로그인-->
          <div class="btn-wrap">
            <button class="btn btn-primary center w-100" @click="done" :class="{ disabled: invalid() }">
              {{ localize("로그인") }}
            </button>
          </div>
          <!-- //로그인-->

          <div class="login-menu">
            <router-link to="/home/join" class="join">{{ localize("회원가입") }}</router-link>
            <router-link to="/home/password/find" class="schPass">{{ localize("비밀번호 찾기") }}</router-link>
          </div>
        </div>
      </div>
      <!-- //container -->
    </main>

    <home-footer />
  </div>
</template>

<style scoped></style>
